import type { GradeShareData, GradingProvider, ProfitInputs, ProviderGradeResult } from '../lib/types'
import { formatGrade, formatMoney } from '../lib/format'
import styles from './report.module.css'

type Outcome = {
  grade: number
  gradeLabel: string
  confidence: number
  chanceLabel: string
  returnValue: number | null
}

const RETURNS_BAR_BASELINE_TOP = 136

export function ReturnsBreakdown({
  data,
  provider,
  result,
  inputs,
}: {
  data: GradeShareData
  provider: GradingProvider
  result: ProviderGradeResult
  inputs?: ProfitInputs
}) {
  if (!inputs) return null

  const outcomes = profitOutcomes(provider, result, inputs)
  if (outcomes.length === 0) return null

  const topOutcomes = [...outcomes]
    .sort((a, b) => b.confidence - a.confidence || b.grade - a.grade)
    .slice(0, 2)
    .sort((a, b) => b.grade - a.grade)
  const mostLikely = [...outcomes].sort((a, b) => b.confidence - a.confidence || b.grade - a.grade)[0]
  const predictedGrade = normalizeGrade(result.grade)
  const verdictOutcome =
    outcomes.find((outcome) => normalizeGrade(outcome.grade) === predictedGrade) ?? mostLikely
  const nearMint = inputs.nearMintPrice
  const predictedProfit =
    verdictOutcome?.returnValue == null || nearMint == null
      ? null
      : Number((verdictOutcome.returnValue - nearMint).toFixed(2))
  const worthGrading = predictedProfit == null ? null : predictedProfit > 0
  const title =
    worthGrading == null ? 'Returns unavailable' : worthGrading ? 'Worth grading' : 'Not worth grading'
  const bullets = verdictBullets({
    worthGrading,
    predictedProfit,
    predictedOutcome: verdictOutcome,
    topOutcomes,
    outcomes,
    mostLikely,
    nearMint,
    provider,
  })
  const chartItems = [
    ...topOutcomes.map((outcome) => ({
      key: `grade-${outcome.grade}`,
      kind: 'grade' as const,
      label: `${provider} ${outcome.gradeLabel}`,
      subLabel: outcome.chanceLabel,
      value: outcome.returnValue,
      valueLabel: formatMoney(outcome.returnValue, { zeroAsDash: true }),
      outcome,
    })),
    {
      key: 'raw',
      kind: 'raw' as const,
      label: 'Ungraded',
      subLabel: 'sell raw',
      value: nearMint,
      valueLabel: formatMoney(nearMint, { zeroAsDash: true }),
      outcome: null,
    },
  ]
  const maxAbs = Math.max(
    1,
    ...chartItems.map((item) =>
      typeof item.value === 'number' && Number.isFinite(item.value) ? Math.abs(item.value) : 0,
    ),
  )
  const rawMostProfitable = worthGrading === false

  return (
    <section className={styles.returnsSection} aria-label="Returns breakdown">
      <div className={styles.returnsHeader}>
        <img
          className={styles.returnsHeaderImage}
          src={data.card.frontImageUrl}
          alt=""
          aria-hidden
        />
        <div className={styles.returnsHeaderText}>
          <h2 className={styles.returnsTitle}>Returns breakdown</h2>
          <p className={styles.returnsSubtitle}>Is it worth grading?</p>
        </div>
      </div>

      <div className={styles.returnsVerdictCard}>
        <div className={styles.returnsVerdictTitleRow}>
          <span
            className={`${styles.returnsVerdictIcon} ${
              worthGrading == null
                ? styles.returnsVerdictNeutral
                : worthGrading
                  ? styles.returnsVerdictPositive
                  : styles.returnsVerdictNegative
            }`}
          >
            {worthGrading == null ? 'i' : worthGrading ? '✓' : '×'}
          </span>
          <h3 className={styles.returnsVerdictTitle}>{title}</h3>
        </div>
        <div className={styles.returnsVerdictBullets}>
          {bullets.map((bullet) => (
            <p key={bullet} className={styles.returnsVerdictBulletRow}>
              <span className={styles.returnsVerdictBulletDot}>•</span>
              <span>{bullet}</span>
            </p>
          ))}
        </div>
      </div>

      <div className={styles.returnsChartSection}>
        <h3 className={styles.returnsChartTitle}>Breakdown by grade</h3>
        <div className={styles.returnsChart}>
          <div className={styles.returnsChartPlot}>
            <div className={styles.returnsChartBaseline} />
            <span className={styles.returnsZeroLabel}>{formatMoney(0)}</span>
            {chartItems.map((item) => {
              const value = typeof item.value === 'number' && Number.isFinite(item.value) ? item.value : null
              const isNegative = (value ?? 0) < 0
              const height = value == null ? 0 : Math.max(10, Math.round((Math.abs(value) / maxAbs) * (isNegative ? 48 : 84)))
              const mostLikelyBar = item.kind === 'grade' && item.outcome?.grade === mostLikely.grade
              const highlighted =
                value != null &&
                (item.kind === 'raw'
                  ? rawMostProfitable
                  : mostLikelyBar && !rawMostProfitable)
              const barStyle = isNegative
                ? { height, top: RETURNS_BAR_BASELINE_TOP }
                : { height, top: RETURNS_BAR_BASELINE_TOP - height }
              const valueStyle = isNegative
                ? { top: RETURNS_BAR_BASELINE_TOP + height + 6 }
                : { top: RETURNS_BAR_BASELINE_TOP - height - 28 }
              const badgeStyle = { top: RETURNS_BAR_BASELINE_TOP - height - 58 }
              return (
                <div key={item.key} className={styles.returnsChartItem}>
                  <div className={styles.returnsBarArea}>
                    {value != null ? (
                      <>
                        <span
                          className={`${styles.returnsBar} ${isNegative ? styles.returnsBarNegative : ''} ${
                            item.kind === 'raw' ? styles.returnsRawBar : ''
                          } ${
                            highlighted
                              ? isNegative
                                ? styles.returnsBarHighlightedNegative
                                : styles.returnsBarHighlighted
                              : ''
                          }`}
                          style={barStyle}
                        />
                        <span
                          className={`${styles.returnsBarValue} ${
                            highlighted
                              ? isNegative
                                ? styles.returnsBarValueNegative
                                : styles.returnsBarValueHighlighted
                              : isNegative
                                ? styles.returnsBarValueTop
                                : styles.returnsBarValueTop
                          }`}
                          style={valueStyle}
                        >
                          {item.valueLabel}
                        </span>
                        {highlighted && !isNegative ? (
                          <span className={styles.returnsMostLikelyBadge} style={badgeStyle}>
                            {item.kind === 'raw' ? 'Most profitable' : 'Most likely'}
                          </span>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>
              )
            })}
          </div>
          <div className={styles.returnsLabelsRow}>
            {chartItems.map((item) => (
              <div key={item.key} className={styles.returnsLabelItem}>
                <span className={styles.returnsBarLabel}>{item.label}</span>
                <span className={styles.returnsBarSubLabel}>{item.subLabel}</span>
              </div>
            ))}
          </div>
        </div>
        <p className={styles.returnsFeeFootnote}>Includes {formatMoney(inputs.gradingFee)} grading fee</p>
      </div>
    </section>
  )
}

function profitOutcomes(
  provider: GradingProvider,
  result: ProviderGradeResult,
  inputs: ProfitInputs,
): Outcome[] {
  return Object.entries(result.confidence_map)
    .map(([label, confidence]) => {
      const grade = Number(label)
      if (!Number.isFinite(grade) || typeof confidence !== 'number' || confidence <= 0) return null
      const price = inputs.gradePrices[String(normalizeGrade(grade))] ?? inputs.gradePrices[label] ?? null
      return {
        grade,
        gradeLabel: formatGrade(grade, provider),
        confidence,
        chanceLabel: `${Math.round(confidence * 100)}% chance`,
        returnValue: price == null ? null : Number((price - inputs.gradingFee).toFixed(2)),
      }
    })
    .filter((outcome): outcome is Outcome => outcome !== null)
}

function verdictBullets({
  worthGrading,
  predictedProfit,
  predictedOutcome,
  topOutcomes,
  outcomes,
  mostLikely,
  nearMint,
  provider,
}: {
  worthGrading: boolean | null
  predictedProfit: number | null
  predictedOutcome: Outcome
  topOutcomes: Outcome[]
  outcomes: Outcome[]
  mostLikely: Outcome
  nearMint: number | null
  provider: GradingProvider
}) {
  if (worthGrading == null || nearMint == null) return ['Missing market data for expected return']
  const bullets: string[] = []
  const delta = predictedOutcome.returnValue == null ? null : predictedOutcome.returnValue - nearMint
  if (worthGrading) {
    if (delta != null && delta > 0) bullets.push('Predicted grade is worth more than ungraded')
    const upside = topOutcomes.find((outcome) => outcome.returnValue != null && outcome.returnValue - nearMint > 0 && outcome.grade > predictedOutcome.grade)
    if (upside) {
      bullets.push(`${Math.round(upside.confidence * 100)}% chance of ${provider} ${upside.gradeLabel} for ${formatMoney(upside.returnValue)} profit`)
    }
    if (bullets.length === 0 && predictedProfit != null) {
      bullets.push(`Predicted grade returns ${formatMoney(Math.abs(predictedProfit))} over ungraded`)
    }
  } else {
    if (delta != null && delta < 0) bullets.push('Predicted grade is worth less than ungraded')
    if (bullets.length === 0 && predictedProfit != null) {
      bullets.push(`Predicted grade returns ${formatMoney(Math.abs(predictedProfit))} under ungraded`)
    }
  }
  if (bullets.length === 0 && mostLikely) {
    const fallback = outcomes.find((outcome) => outcome.returnValue != null)
    if (fallback) bullets.push(`${fallback.chanceLabel} of ${provider} ${fallback.gradeLabel}`)
  }
  return bullets
}

function normalizeGrade(grade: number) {
  return grade === 11 ? 10 : grade
}
